import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toastr from "toastr";

import Breadcrumb from "../../../components/Common/Breadcrumb";
import TableContainer from "../../../components/Common/TableContainer";
import Paginations from "../../../components/Common/Pagination";
import Spinners from "../../../components/Common/Spinner";

import {
  useCreateDiscount,
  useGetAllDiscounts,
  useToggleDiscountStatus,
  useUpdateDiscount
} from "../../../api/discounts";

const INTEGER_RE = /^\d+$/;
const DECIMAL_RE = /^\d+(\.\d{1,2})?$/;

const CREATE_SCHEMA = Yup.object({
  numberOfStaff: Yup.string()
    .trim()
    .required("Number of staff is required")
    .test("is-integer", "Number of staff must be a whole number", (value) =>
      INTEGER_RE.test(value || "")
    ),
  discountValue: Yup.string()
    .trim()
    .required("Discount value is required")
    .test("is-decimal", "Discount must be a number (up to two decimals)", (value) =>
      DECIMAL_RE.test(value || "")
    ),
  discountType: Yup.string()
    .oneOf(["percentage", "fixed"], "Invalid discount type")
    .required("Discount type is required")
});

const UPDATE_SCHEMA = CREATE_SCHEMA;

const resolveDiscountPayload = (payload) => {
  if (!payload) return { list: [], total: 0 };

  // If payload has a `data` object wrapper, unwrap it once
  const primary = payload?.data && !Array.isArray(payload.data) ? payload.data : payload;

  // 1. handle shape: { status, data: [...] , totalRecords }
  if (Array.isArray(primary?.data)) {
    return {
      list: primary.data,
      total:
        primary.totalRecords ?? primary.total ?? primary.data.length ?? payload.totalRecords ?? 0
    };
  }

  // 2. handle shape directly returning array
  if (Array.isArray(primary)) {
    return { list: primary, total: primary.length };
  }

  // 3. handle shape: { discounts: [...], total }
  if (Array.isArray(primary?.discounts)) {
    return {
      list: primary.discounts,
      total:
        primary.total ??
        primary.totalRecords ??
        primary.discounts.length ??
        payload.total ??
        payload.totalRecords ??
        0
    };
  }

  return {
    list: [],
    total: primary?.totalRecords ?? primary?.total ?? payload?.totalRecords ?? payload?.total ?? 0
  };
};

const DiscountList = () => {
  document.title = "Discounts | JobArk Admin";

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(null);

  const getDiscounts = useGetAllDiscounts();
  const createDiscount = useCreateDiscount();
  const updateDiscount = useUpdateDiscount();
  const toggleDiscount = useToggleDiscountStatus();
  const { mutate: toggleMutate, isPending: togglePending } = toggleDiscount;

  const { mutate, data, isLoading, isPending, error } = getDiscounts;
  const loading = isLoading || isPending;

  const lastFetchKeyRef = useRef("");
  const fetchKey = JSON.stringify({
    page,
    limit,
    search
  });

  useEffect(() => {
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    mutate({
      page,
      limit,
      search: search.trim() || undefined
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey]);

  const { list: discounts, total } = resolveDiscountPayload(data);

  const refresh = useCallback(() => {
    mutate({
      page,
      limit,
      search: search.trim() || undefined
    });
  }, [mutate, page, limit, search]);

  const createFormik = useFormik({
    enableReinitialize: true,
    validationSchema: CREATE_SCHEMA,
    initialValues: {
      numberOfStaff: "",
      discountValue: "",
      discountType: "percentage"
    },
    onSubmit: (values, { resetForm }) => {
      createDiscount.mutate(
        {
          numberOfStaff: values.numberOfStaff.trim(),
          discountValue: values.discountValue.trim(),
          discountType: values.discountType
        },
        {
          onSuccess: () => {
            toastr.success("Discount created");
            resetForm();
            setShowCreate(false);
            refresh();
          }
        }
      );
    }
  });

  const editFormik = useFormik({
    enableReinitialize: true,
    validationSchema: UPDATE_SCHEMA,
    initialValues: {
      numberOfStaff: editing?.numberOfStaff ? String(editing.numberOfStaff) : "",
      discountValue: editing?.discountValue ? String(editing.discountValue) : "",
      discountType: editing?.discountType || "percentage"
    },
    onSubmit: (values) => {
      if (!editing?.slug) return;
      updateDiscount.mutate(
        {
          slug: editing.slug,
          numberOfStaff: values.numberOfStaff.trim(),
          discountValue: values.discountValue.trim(),
          discountType: values.discountType
        },
        {
          onSuccess: () => {
            toastr.success("Discount updated");
            setShowEdit(false);
            setEditing(null);
            refresh();
          }
        }
      );
    }
  });

  const columns = useMemo(
    () => [
      {
        header: "Staff Count",
        accessorKey: "numberOfStaff",
        enableColumnFilter: false,
        cell: (info) => info.getValue() ?? "-"
      },
      {
        header: "Value",
        accessorKey: "discountValue",
        enableColumnFilter: false,
        cell: (info) => {
          const discount = info.row.original;
          const value = info.getValue();
          if (!value && value !== 0) return "-";
          if (discount.discountType === "percentage") {
            return `${value}%`;
          }
          return `£${value}`;
        }
      },
      {
        header: "Type",
        accessorKey: "discountType",
        enableColumnFilter: false,
        cell: (info) => {
          const type = info.getValue();
          return (
            <Badge className="bg-info text-uppercase">
              {type === "fixed" ? "Fixed" : "Percentage"}
            </Badge>
          );
        }
      },
      {
        header: "Status",
        accessorKey: "isActive",
        enableColumnFilter: false,
        cell: (info) =>
          info.getValue() ? (
            <Badge className="bg-success">Active</Badge>
          ) : (
            <Badge className="bg-secondary">Inactive</Badge>
          )
      },
      {
        header: "Created",
        accessorKey: "createdAt",
        enableColumnFilter: false,
        cell: (info) => {
          const date = info.getValue();
          if (!date) return "-";
          const d = new Date(date);
          return (
            <div className="text-muted">
              <div>{d.toLocaleDateString()}</div>
              <small>{d.toLocaleTimeString()}</small>
            </div>
          );
        }
      },
      {
        header: "Updated",
        accessorKey: "updatedAt",
        enableColumnFilter: false,
        cell: (info) => {
          const date = info.getValue();
          if (!date) return "-";
          const d = new Date(date);
          return (
            <div className="text-muted">
              <div>{d.toLocaleDateString()}</div>
              <small>{d.toLocaleTimeString()}</small>
            </div>
          );
        }
      },
      {
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          const discount = cellProps.row.original;
          const isActive = !!discount.isActive;
          const toggleLabel = isActive ? "Deactivate" : "Activate";
          const toggleColor = isActive ? "soft-danger" : "soft-success";
          return (
            <div className="d-flex gap-2">
              <Button
                size="sm"
                color="soft-info"
                onClick={() => {
                  setEditing(discount);
                  setShowEdit(true);
                }}
              >
                <i className="mdi mdi-pencil-outline me-1" />
                Edit
              </Button>
              <Button
                size="sm"
                color={toggleColor}
                disabled={togglePending}
                onClick={() => {
                  if (!discount.slug) return;
                  toggleMutate(
                    {
                      slug: discount.slug,
                      status: !isActive
                    },
                    {
                      onSuccess: () => {
                        toastr.success(`Discount ${!isActive ? "activated" : "deactivated"}`);
                        refresh();
                      }
                    }
                  );
                }}
              >
                {togglePending ? (
                  <i className="mdi mdi-loading mdi-spin" />
                ) : (
                  <i
                    className={`mdi ${
                      isActive ? "mdi-toggle-switch-off-outline" : "mdi-toggle-switch"
                    } me-1`}
                  />
                )}
                {toggleLabel}
              </Button>
            </div>
          );
        }
      }
    ],
    [refresh, toggleMutate, togglePending]
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Admin" breadcrumbItem="Discounts" />

        <Row className="mb-3 g-2 align-items-center">
          <Col md={5}>
            <Input
              type="text"
              placeholder="Search by staff count..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPage(1);
                  setSearch(searchInput.trim());
                }
              }}
            />
          </Col>

          <Col md="auto">
            <Button color="primary" onClick={() => setShowCreate(true)}>
              <i className="mdi mdi-plus-circle-outline me-1" />
              New Discount
            </Button>
          </Col>
        </Row>

        <Row className="mb-3 g-2 align-items-center">
          <Col md="auto" className="ms-auto d-flex gap-2 align-items-center">
            <Button
              color="primary"
              onClick={() => {
                setPage(1);
                setSearch(searchInput.trim());
              }}
              disabled={loading}
            >
              <i className="mdi mdi-magnify me-1" />
              Search
            </Button>
            <Button
              color="secondary"
              outline
              onClick={() => {
                setSearch("");
                setSearchInput("");
                setPage(1);
              }}
              disabled={loading}
            >
              Reset
            </Button>
            {/* Page size */}
            <Input
              type="select"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              style={{ width: "auto", minWidth: "100px" }}
            >
              {[10, 20].map((n) => (
                <option key={n} value={n}>
                  Show {n}
                </option>
              ))}
            </Input>
            <Button
              color="light"
              onClick={() =>
                mutate({
                  page,
                  limit,
                  search: search.trim() || undefined
                })
              }
              disabled={loading}
            >
              <i className="mdi mdi-refresh" />
            </Button>
          </Col>
        </Row>

        <Card>
          <CardBody>
            {loading && discounts.length === 0 ? (
              <Spinners setLoading={() => {}} />
            ) : error ? (
              <div className="text-danger">Failed to load discounts.</div>
            ) : discounts.length === 0 ? (
              <div className="text-center text-muted py-5">No discounts found.</div>
            ) : (
              <>
                <TableContainer
                  columns={columns}
                  data={discounts}
                  isPagination={false}
                  isCustomPageSize={false}
                  isGlobalFilter={false}
                  isJobListGlobalFilter={false}
                  tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-2 border-top"
                  theadClass="table-light"
                  divClassName="table-responsive"
                />

                <Paginations
                  total={total}
                  perPageData={limit}
                  currentPage={page}
                  setCurrentPage={setPage}
                  isShowingPageLength
                  paginationDiv="dataTables_paginate paging_simple_numbers pagination-rounded d-flex justify-content-end mt-3"
                  paginationClass="pagination"
                />
              </>
            )}
          </CardBody>
        </Card>

        <Modal isOpen={showCreate} toggle={() => setShowCreate((v) => !v)} centered>
          <Form onSubmit={createFormik.handleSubmit}>
            <ModalHeader toggle={() => setShowCreate(false)}>Create Discount</ModalHeader>
            <ModalBody>
              <Row className="g-3">
                <Col md={12}>
                  <Label className="form-label" for="create-numberOfStaff">
                    Number of Staff
                  </Label>
                  <Input
                    id="create-numberOfStaff"
                    name="numberOfStaff"
                    value={createFormik.values.numberOfStaff}
                    onChange={createFormik.handleChange}
                    onBlur={createFormik.handleBlur}
                    invalid={
                      createFormik.touched.numberOfStaff && !!createFormik.errors.numberOfStaff
                    }
                    placeholder="e.g. 10"
                  />
                  {createFormik.touched.numberOfStaff && createFormik.errors.numberOfStaff && (
                    <FormFeedback type="invalid">{createFormik.errors.numberOfStaff}</FormFeedback>
                  )}
                </Col>

                <Col md={12}>
                  <Label className="form-label" for="create-discountValue">
                    Discount Value
                  </Label>
                  <Input
                    id="create-discountValue"
                    name="discountValue"
                    value={createFormik.values.discountValue}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/,/g, ".");
                      if (!raw || DECIMAL_RE.test(raw)) {
                        createFormik.setFieldValue("discountValue", raw);
                      }
                    }}
                    onBlur={createFormik.handleBlur}
                    invalid={
                      createFormik.touched.discountValue && !!createFormik.errors.discountValue
                    }
                    placeholder="e.g. 5 or 10.5"
                  />
                  {createFormik.touched.discountValue && createFormik.errors.discountValue && (
                    <FormFeedback type="invalid">{createFormik.errors.discountValue}</FormFeedback>
                  )}
                </Col>

                <Col md={12}>
                  <Label className="form-label" for="create-discountType">
                    Discount Type
                  </Label>
                  <Input
                    type="select"
                    id="create-discountType"
                    name="discountType"
                    value={createFormik.values.discountType}
                    onChange={createFormik.handleChange}
                    onBlur={createFormik.handleBlur}
                    invalid={
                      createFormik.touched.discountType && !!createFormik.errors.discountType
                    }
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed</option>
                  </Input>
                  {createFormik.touched.discountType && createFormik.errors.discountType && (
                    <FormFeedback type="invalid">{createFormik.errors.discountType}</FormFeedback>
                  )}
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter className="d-flex justify-content-between">
              <Button type="button" color="light" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button type="submit" color="success" disabled={createDiscount.isPending}>
                {createDiscount.isPending ? (
                  <>
                    <i className="mdi mdi-loading mdi-spin me-1" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="mdi mdi-content-save-outline me-1" />
                    Save
                  </>
                )}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        <Modal isOpen={showEdit} toggle={() => setShowEdit((v) => !v)} centered>
          <Form onSubmit={editFormik.handleSubmit}>
            <ModalHeader toggle={() => setShowEdit(false)}>Edit Discount</ModalHeader>
            <ModalBody>
              <Row className="g-3">
                <Col md={12}>
                  <Label className="form-label" for="edit-numberOfStaff">
                    Number of Staff
                  </Label>
                  <Input
                    id="edit-numberOfStaff"
                    name="numberOfStaff"
                    value={editFormik.values.numberOfStaff}
                    onChange={editFormik.handleChange}
                    onBlur={editFormik.handleBlur}
                    invalid={editFormik.touched.numberOfStaff && !!editFormik.errors.numberOfStaff}
                  />
                  {editFormik.touched.numberOfStaff && editFormik.errors.numberOfStaff && (
                    <FormFeedback type="invalid">{editFormik.errors.numberOfStaff}</FormFeedback>
                  )}
                </Col>

                <Col md={12}>
                  <Label className="form-label" for="edit-discountValue">
                    Discount Value
                  </Label>
                  <Input
                    id="edit-discountValue"
                    name="discountValue"
                    value={editFormik.values.discountValue}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/,/g, ".");
                      if (!raw || DECIMAL_RE.test(raw)) {
                        editFormik.setFieldValue("discountValue", raw);
                      }
                    }}
                    onBlur={editFormik.handleBlur}
                    invalid={editFormik.touched.discountValue && !!editFormik.errors.discountValue}
                  />
                  {editFormik.touched.discountValue && editFormik.errors.discountValue && (
                    <FormFeedback type="invalid">{editFormik.errors.discountValue}</FormFeedback>
                  )}
                </Col>

                <Col md={12}>
                  <Label className="form-label" for="edit-discountType">
                    Discount Type
                  </Label>
                  <Input
                    type="select"
                    id="edit-discountType"
                    name="discountType"
                    value={editFormik.values.discountType}
                    onChange={editFormik.handleChange}
                    onBlur={editFormik.handleBlur}
                    invalid={editFormik.touched.discountType && !!editFormik.errors.discountType}
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed</option>
                  </Input>
                  {editFormik.touched.discountType && editFormik.errors.discountType && (
                    <FormFeedback type="invalid">{editFormik.errors.discountType}</FormFeedback>
                  )}
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter className="d-flex justify-content-between">
              <Button
                type="button"
                color="light"
                onClick={() => {
                  setShowEdit(false);
                  setEditing(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={updateDiscount.isPending}>
                {updateDiscount.isPending ? (
                  <>
                    <i className="mdi mdi-loading mdi-spin me-1" />
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="mdi mdi-content-save-outline me-1" />
                    Update
                  </>
                )}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default DiscountList;
